import React, { useState, useEffect, useRef } from "react";
import Avatar from "../../components/ui/Avatar";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import BaseModal from "../../components/modals/BaseModal";
import Input from "../../components/forms/Input";
import Badge from "../../components/ui/Badge";
import { useModal } from "../../hooks/useModal";
import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useToast from "../../hooks/useToast";
import * as ticketService from "../../api/ticketService";
import logger from "../../utils/logger";
import { getTicketDisplayId } from "../../utils/ticketUtils";
import { uploadFile, IMAGES_BUCKET } from "../../api/supabaseStorage";
import JSZip from "jszip";
import {
  Plus,
  Search,
  Paperclip,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  MessageSquare,
  Ticket,
  Loader2,
  Info,
  FileText,
  Download,
  FolderOpen,
  Pencil,
} from "lucide-react";
import { motion } from "framer-motion";

// Regex that matches http/https URLs
const URL_REGEX = /(https?:\/\/[^\s<>"'()]+[^\s<>"'().,:;!?])/g;

/**
 * Split text into plain strings and clickable URL fragments.
 */
const renderMessageText = (text) => {
  if (!text) return null;
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-primary-accent dark:text-orange-400 break-all hover:opacity-80 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
};

const Tickets = () => {
  const { isOpen, openModal, closeModal } = useModal();
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { showToast } = useToast();
  const [ticketsData, setTicketsData] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newTicketData, setNewTicketData] = useState({
    title: "",
    description: "",
    assignedTo: [],
    assignedTeams: [],
  });
  const [assignType, setAssignType] = useState("employees"); // "employees" or "teams"
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [alreadyClosedModal, setAlreadyClosedModal] = useState({
    open: false,
    ticket: null,
  });
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isSendingRef = useRef(false); // guards against double-send on rapid Enter presses
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [folderConfirmation, setFolderConfirmation] = useState(null); // { name, fileCount, files: File[] } | null
  const [folderZipping, setFolderZipping] = useState(false);
  const [folderUploading, setFolderUploading] = useState(false);
  const folderInputRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [teams] = useState([
    { value: "backend", label: "Backend Team" },
    { value: "frontend", label: "Frontend Team" },
    { value: "mobile_app", label: "Mobile App Team" },
    { value: "uiux", label: "UI/UX Team" },
    { value: "tester", label: "Tester Team" },
    { value: "ra", label: "RA Team" },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [viewedTickets, setViewedTickets] = useState(new Set());
  const [showDetails, setShowDetails] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [isAddingParticipants, setIsAddingParticipants] = useState(false);
  const [participantSearchQuery, setParticipantSearchQuery] = useState("");

  // Inline message editing
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Get current user info
  const currentUserId = auth?.employee?._id;
  const isAdmin = Array.isArray(auth?.employee?.teams)
    ? auth.employee.teams.some((t) => t.team === "high_potit")
    : auth?.employee?.team === "high_potit";

  // Fetch tickets and employees on mount
  useEffect(() => {
    fetchTickets();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for real-time ticket updates via Firebase notifications
  useEffect(() => {
    const handleTicketAssigned = (event) => {
      const { ticketData, ticketId } = event.detail;

      // Check if this ticket should be visible to current user
      // (creator, assigned user, or admin)
      if (ticketData) {
        const ticketCreatedById =
          ticketData.createdBy?._id?.toString() ||
          ticketData.createdBy?.toString() ||
          ticketData.createdBy;
        const currentUserIdStr = currentUserId?.toString();
        const isCreator = ticketCreatedById === currentUserIdStr;

        const isAssignedIndividually = ticketData.assignedTo?.some((emp) => {
          const empId = emp._id?.toString() || emp?.toString() || emp;
          return empId === currentUserIdStr;
        });

        const userTeams = Array.isArray(auth?.employee?.teams)
          ? auth.employee.teams.map((t) => t.team)
          : auth?.employee?.team
            ? [auth.employee.team]
            : [];
        const isAssignedByTeam = ticketData.assignedTeams?.some((at) =>
          userTeams.includes(at),
        );

        const shouldShow =
          isCreator || isAssignedIndividually || isAssignedByTeam || isAdmin;

        if (shouldShow) {
          // Use functional update to check if ticket exists and add it
          setTicketsData((prev) => {
            const newTicketId = (ticketData._id || ticketId)?.toString();
            const ticketExists = prev.some((t) => {
              const tId = t._id?.toString();
              return tId === newTicketId;
            });

            if (!ticketExists) {
              // Add new ticket to the beginning of list (newest first)
              const newTicket = {
                ...ticketData,
                _id: ticketData._id || ticketId,
              };
              return [newTicket, ...prev];
            } else {
              return prev;
            }
          });
        }
      } else {
        fetchTickets();
      }
    };

    // Listen for ticket-assigned event
    window.addEventListener("ticket-assigned", handleTicketAssigned);

    return () => {
      window.removeEventListener("ticket-assigned", handleTicketAssigned);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, isAdmin, auth?.employee?.teams, auth?.employee?.team]);

  // Listen for real-time ticket message updates via Firebase notifications
  useEffect(() => {
    const handleTicketMessage = (event) => {
      const { ticketData, ticketId, messageId, sentBy } = event.detail;

      // Don't update if message was sent by current user (already handled locally)
      const sentById = sentBy?.toString();
      const currentUserIdStr = currentUserId?.toString();
      if (sentById === currentUserIdStr) return;

      // Only update if this ticket is currently selected
      if (
        selectedTicket &&
        (selectedTicket._id?.toString() === ticketId ||
          selectedTicket._id === ticketId)
      ) {
        if (ticketData && ticketData.messages) {
          // Check if message already exists (avoid duplicates)
          const messageExists = selectedTicket.messages?.some(
            (msg) => msg._id?.toString() === messageId || msg._id === messageId,
          );

          if (!messageExists) {
            // Update selected ticket with new message
            setSelectedTicket((prev) => {
              if (!prev || prev._id?.toString() !== ticketId) {
                return prev;
              }

              // Find the new message in ticketData
              const newMessage = ticketData.messages.find(
                (msg) =>
                  msg._id?.toString() === messageId || msg._id === messageId,
              );

              if (newMessage) {
                // Add new message to the ticket
                const updatedMessages = [...(prev.messages || []), newMessage];
                return {
                  ...prev,
                  messages: updatedMessages,
                  updatedAt: ticketData.updatedAt || new Date(),
                };
              }

              // If message not found, use full ticketData
              return ticketData;
            });

            // Also update the ticket in the list
            setTicketsData((prev) =>
              prev.map((t) => {
                if (t._id?.toString() === ticketId || t._id === ticketId) {
                  return ticketData;
                }
                return t;
              }),
            );

            // Scroll to bottom to show new message
            setTimeout(() => {
              chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
          }
        } else {
          handleSelectTicket(selectedTicket);
        }
      }
    };

    // Listen for ticket-message event
    window.addEventListener("ticket-message", handleTicketMessage);

    return () => {
      window.removeEventListener("ticket-message", handleTicketMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket, currentUserId]);

  // Cancel any active edit when the selected ticket becomes non-open
  useEffect(() => {
    if (editingMessageId && selectedTicket?.status !== "open") {
      setEditingMessageId(null);
      setEditingText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.status]);

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const data = await ticketService.getAllTickets();
      setTicketsData(data.tickets || []);
      // Do not auto-select any ticket; user must click to view
    } catch (error) {
      logger.error("Failed to fetch tickets:", error);
      showToast("Failed to load tickets", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axiosPrivate.get("/api/employees");
      const employeeList = response.data || [];
      // Add fullName property for display
      const employeesWithFullName = employeeList.map((emp) => ({
        ...emp,
        fullName: emp.fullNameEn || emp.nickname || "Unknown",
      }));
      setEmployees(employeesWithFullName);
    } catch (error) {
      logger.error("Failed to fetch employees:", error);
      showToast("Failed to load employees", "error");
    }
  };

  // Handle ticket selection — reuse loaded data when same ticket is clicked
  const handleSelectTicket = async (ticket) => {
    if (selectedTicket?._id === ticket._id) return;

    try {
      const data = await ticketService.getTicketById(ticket._id);
      setSelectedTicket(data.ticket);

      // Mark ticket as viewed
      setViewedTickets((prev) => {
        const newSet = new Set(prev);
        newSet.add(data.ticket._id.toString());
        return newSet;
      });

      setTicketsData((prev) =>
        prev.map((t) => (t._id === data.ticket._id ? data.ticket : t)),
      );
    } catch (error) {
      logger.error("Failed to fetch ticket details:", error);
      showToast("Failed to load ticket details", "error");
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      open: "danger",
      in_progress: "warning",
      closed: "success",
    };
    return variants[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: "Open",
      in_progress: "In Progress",
      closed: "Closed",
    };
    return labels[status] || status;
  };

  const filteredTickets = ticketsData.filter((ticket) =>
    ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const creatorId = (ticket) =>
    ticket &&
    (typeof ticket.createdBy === "object"
      ? ticket.createdBy?._id
      : ticket.createdBy);
  const closeRequestedById = (ticket) =>
    ticket?.closeRequestedBy &&
    (typeof ticket.closeRequestedBy === "object"
      ? ticket.closeRequestedBy._id
      : ticket.closeRequestedBy);
  const isTicketCreator = (ticket) => creatorId(ticket) === currentUserId;
  const isCloseRequestedByMe = (ticket) =>
    closeRequestedById(ticket) === currentUserId;

  const handleCloseTicketClick = async (ticketId) => {
    try {
      const { ticket: fresh } = await ticketService.getTicketById(ticketId);
      if (fresh.status === "closed") {
        setSelectedTicket(fresh);
        setTicketsData((prev) =>
          prev.map((t) => (t._id === fresh._id ? fresh : t)),
        );
        setAlreadyClosedModal({ open: true, ticket: fresh });
        return;
      }

      const isCreator = isTicketCreator(fresh);

      if (isCreator) {
        if (window.confirm("Are you sure you want to close this ticket?")) {
          await doCloseTicket(ticketId);
        }
        return;
      }

      await doRequestClose(ticketId);
    } catch (error) {
      logger.error("Failed to check ticket:", error);
      showToast("Failed to load ticket status", "error");
    }
  };

  const doCloseTicket = async (ticketId) => {
    try {
      const data = await ticketService.closeTicket(ticketId);
      showToast("Ticket closed successfully", "success");
      setTicketsData((prev) =>
        prev.map((t) => (t._id === ticketId ? data.ticket : t)),
      );
      if (selectedTicket?._id === ticketId) {
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      logger.error("Failed to close ticket:", error);
      showToast(
        error.response?.data?.message || "Failed to close ticket",
        "error",
      );
    }
  };

  const doRequestClose = async (ticketId) => {
    try {
      const data = await ticketService.requestClose(ticketId);
      showToast(
        "Close requested. Waiting for ticket creator to confirm or cancel.",
        "success",
      );
      setTicketsData((prev) =>
        prev.map((t) => (t._id === ticketId ? data.ticket : t)),
      );
      if (selectedTicket?._id === ticketId) {
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      logger.error("Failed to request close:", error);
      showToast(
        error.response?.data?.message || "Failed to request close",
        "error",
      );
    }
  };

  const handleConfirmClose = async (ticketId) => {
    try {
      const data = await ticketService.confirmClose(ticketId);
      showToast("Ticket closed successfully", "success");
      setTicketsData((prev) =>
        prev.map((t) => (t._id === ticketId ? data.ticket : t)),
      );
      if (selectedTicket?._id === ticketId) {
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      logger.error("Failed to confirm close:", error);
      showToast(
        error.response?.data?.message || "Failed to confirm close",
        "error",
      );
    }
  };

  const handleCancelClose = async (ticketId) => {
    try {
      const data = await ticketService.cancelClose(ticketId);
      showToast(
        "Close request cancelled. Assignee can send messages again.",
        "success",
      );
      setTicketsData((prev) =>
        prev.map((t) => (t._id === ticketId ? data.ticket : t)),
      );
      if (selectedTicket?._id === ticketId) {
        setSelectedTicket(data.ticket);
      }
    } catch (error) {
      logger.error("Failed to cancel close:", error);
      showToast(
        error.response?.data?.message || "Failed to cancel close",
        "error",
      );
    }
  };

  const handleAlreadyClosedCloseTicket = () => {
    const t = alreadyClosedModal.ticket;
    setTicketsData((prev) =>
      prev.map((item) => (item._id === t._id ? t : item)),
    );
    if (selectedTicket?._id === t._id) {
      setSelectedTicket(null);
    }
    setAlreadyClosedModal({ open: false, ticket: null });
  };

  const handleAlreadyClosedContinue = () => {
    if (alreadyClosedModal.ticket) {
      setSelectedTicket(alreadyClosedModal.ticket);
      setTicketsData((prev) =>
        prev.map((t) =>
          t._id === alreadyClosedModal.ticket._id
            ? alreadyClosedModal.ticket
            : t,
        ),
      );
    }
    setAlreadyClosedModal({ open: false, ticket: null });
  };

  const handleSendMessage = async () => {
    const text = newMessage.trim();
    if ((!text && !attachmentFile) || !selectedTicket) return;
    if (!canSendMessage(selectedTicket)) {
      showToast(
        selectedTicket.status === "closed"
          ? "Cannot send messages on a closed ticket"
          : "You cannot send messages until the creator confirms or cancels the close request.",
        "error",
      );
      return;
    }
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    try {
      let payload = { text: text || "" };
      if (attachmentFile) {
        setIsUploadingFile(true);
        const safeFileName = attachmentFile.name.replace(
          /[^a-zA-Z0-9._-]/g,
          "_",
        );
        const path = `tickets/${selectedTicket._id}/${Date.now()}-${safeFileName}`;
        const { publicUrl, error: uploadError } = await uploadFile(
          IMAGES_BUCKET,
          path,
          attachmentFile,
        );
        setIsUploadingFile(false);
        if (uploadError) {
          showToast(uploadError.message || "File upload failed", "error");
          isSendingRef.current = false;
          return;
        }
        payload.attachmentUrl = publicUrl;
        payload.attachmentFilename = attachmentFile.name;
      }
      const data = await ticketService.addMessage(selectedTicket._id, payload);
      setSelectedTicket(data.ticket);
      setNewMessage("");
      setAttachmentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTicketsData((prev) =>
        prev.map((t) => (t._id === data.ticket._id ? data.ticket : t)),
      );
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      logger.error("Failed to send message:", error);
      showToast(
        error.response?.data?.message || "Failed to send message",
        "error",
      );
    } finally {
      isSendingRef.current = false;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentFile(file);
  };

  const handleFolderChange = (e) => {
    const allFiles = Array.from(e.target.files || []);
    if (allFiles.length === 0) return;
    const folderName = (
      allFiles[0].webkitRelativePath?.split("/")[0] || "folder"
    ).replace(/[^a-zA-Z0-9._-]/g, "_");
    e.target.value = "";
    setFolderConfirmation({
      name: folderName,
      fileCount: allFiles.length,
      files: allFiles,
    });
  };

  const clearFolderFiles = () => setFolderConfirmation(null);

  const handleConfirmFolder = async () => {
    if (!folderConfirmation || !selectedTicket) return;
    if (!canSendMessage(selectedTicket)) {
      showToast("Cannot send messages on this ticket", "error");
      setFolderConfirmation(null);
      return;
    }
    const { name: folderName, files: allFiles } = folderConfirmation;
    setFolderConfirmation(null);
    setFolderZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(folderName);
      for (const file of allFiles) {
        const relativePath = file.webkitRelativePath || file.name;
        const pathInZip =
          relativePath.split("/").slice(1).join("/") || file.name;
        folder.file(pathInZip, file);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      setFolderZipping(false);
      setFolderUploading(true);
      const zipFileName = `${folderName}.zip`;
      const zipFile = new File([zipBlob], zipFileName, {
        type: "application/zip",
      });
      const path = `tickets/${selectedTicket._id}/${Date.now()}-${zipFileName}`;
      const { publicUrl, error: uploadError } = await uploadFile(
        IMAGES_BUCKET,
        path,
        zipFile,
      );
      if (uploadError) {
        showToast(uploadError.message || "Upload failed", "error");
        setFolderUploading(false);
        return;
      }
      const data = await ticketService.addMessage(selectedTicket._id, {
        text: "",
        attachmentUrl: publicUrl,
        attachmentFilename: zipFileName,
      });
      setSelectedTicket(data.ticket);
      setTicketsData((prev) =>
        prev.map((t) => (t._id === data.ticket._id ? data.ticket : t)),
      );
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      showToast("Failed to send folder", "error");
    } finally {
      setFolderZipping(false);
      setFolderUploading(false);
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Save an edited message — only allowed while ticket is open
  const handleSaveEdit = async (messageId) => {
    const text = editingText.trim();
    if (!text || !selectedTicket) return;
    try {
      const data = await ticketService.editMessage(
        selectedTicket._id,
        messageId,
        text,
      );
      setSelectedTicket(data.ticket);
      setTicketsData((prev) =>
        prev.map((t) => (t._id === data.ticket._id ? data.ticket : t)),
      );
      setEditingMessageId(null);
      setEditingText("");
    } catch (error) {
      logger.error("Failed to edit message:", error);
      showToast(
        error.response?.data?.message || "Failed to edit message",
        "error",
      );
    }
  };

  // Check if current user can close / request close
  const canCloseTicket = (ticket) => {
    if (!ticket || ticket.status === "closed") return false;
    if (closeRequestedById(ticket) === currentUserId) return false;
    if (isAdmin) return true;

    // Ticket creator can close their own ticket
    if (isTicketCreator(ticket)) return true;

    const assignedIds =
      ticket.assignedTo?.map((emp) =>
        typeof emp === "string" ? emp : emp._id,
      ) || [];
    const isAssignedIndividually = assignedIds.includes(currentUserId);
    const userTeams = Array.isArray(auth?.employee?.teams)
      ? auth.employee.teams.map((t) => t.team)
      : auth?.employee?.team
        ? [auth.employee.team]
        : [];
    const isAssignedByTeam = ticket.assignedTeams?.some((at) =>
      userTeams.includes(at),
    );

    return isAssignedIndividually || isAssignedByTeam;
  };

  const hasPendingCloseRequest = (ticket) => !!closeRequestedById(ticket);
  const canSendMessage = (ticket) => {
    if (!ticket || ticket.status === "closed") return false;
    if (hasPendingCloseRequest(ticket) && isCloseRequestedByMe(ticket))
      return false;
    return true;
  };

  const toggleAssignee = (employeeId) => {
    const isSelected = newTicketData.assignedTo.includes(employeeId);
    if (isSelected) {
      setNewTicketData((prev) => ({
        ...prev,
        assignedTo: prev.assignedTo.filter((id) => id !== employeeId),
      }));
    } else {
      setNewTicketData((prev) => ({
        ...prev,
        assignedTo: [...prev.assignedTo, employeeId],
      }));
    }
  };

  const removeAssignee = (employeeId) => {
    setNewTicketData((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.filter((id) => id !== employeeId),
    }));
  };

  const toggleTeam = (teamValue) => {
    const isSelected = newTicketData.assignedTeams.includes(teamValue);
    if (isSelected) {
      setNewTicketData((prev) => ({
        ...prev,
        assignedTeams: prev.assignedTeams.filter((team) => team !== teamValue),
      }));
    } else {
      setNewTicketData((prev) => ({
        ...prev,
        assignedTeams: [...prev.assignedTeams, teamValue],
      }));
    }
  };

  const removeTeam = (teamValue) => {
    setNewTicketData((prev) => ({
      ...prev,
      assignedTeams: prev.assignedTeams.filter((team) => team !== teamValue),
    }));
  };

  const handleCreateTicket = async () => {
    if (!newTicketData.title.trim()) {
      showToast("Please enter a ticket title", "error");
      return;
    }
    if (!newTicketData.description.trim()) {
      showToast("Please enter a ticket description", "error");
      return;
    }
    if (isCreatingTicket) return;

    setIsCreatingTicket(true);
    try {
      await ticketService.createTicket({
        title: newTicketData.title,
        description: newTicketData.description,
        assignedTo: newTicketData.assignedTo,
        assignedTeams: newTicketData.assignedTeams,
      });

      showToast("Ticket created successfully", "success");
      closeModal();
      setNewTicketData({
        title: "",
        description: "",
        assignedTo: [],
        assignedTeams: [],
      });
      setAssignType("employees");

      fetchTickets();
    } catch (error) {
      logger.error("Failed to create ticket:", error);
      showToast(
        error.response?.data?.message || "Failed to create ticket",
        "error",
      );
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const handleOpenParticipantsModal = () => {
    if (!selectedTicket) return;
    setSelectedParticipants([]);
    setParticipantSearchQuery("");
    setIsParticipantsModalOpen(true);
  };

  const handleCloseParticipantsModal = () => {
    if (isAddingParticipants) return;
    setIsParticipantsModalOpen(false);
    setSelectedParticipants([]);
    setParticipantSearchQuery("");
  };

  const toggleParticipant = (employeeId) => {
    const isSelected = selectedParticipants.includes(employeeId);
    if (isSelected) {
      setSelectedParticipants((prev) => prev.filter((id) => id !== employeeId));
    } else {
      setSelectedParticipants((prev) => [...prev, employeeId]);
    }
  };

  const handleAddParticipants = async () => {
    if (!selectedTicket || selectedParticipants.length === 0) {
      showToast("Please select at least one participant", "error");
      return;
    }

    setIsAddingParticipants(true);
    try {
      const data = await ticketService.addParticipants(
        selectedTicket._id,
        selectedParticipants,
      );

      showToast("Participants added successfully", "success");
      setSelectedTicket(data.ticket);
      setTicketsData((prev) =>
        prev.map((t) => (t._id === data.ticket._id ? data.ticket : t)),
      );
      handleCloseParticipantsModal();
    } catch (error) {
      logger.error("Failed to add participants:", error);
      showToast(
        error.response?.data?.message || "Failed to add participants",
        "error",
      );
    } finally {
      setIsAddingParticipants(false);
    }
  };

  return (
    <PageWrapper
      title="Support Tickets"
      subtitle="Contact our support team anytime and track your tickets."
      actions={
        <Button onClick={openModal} className="shadow-sm">
          <Plus className="w-4 h-4" />
          Create New Ticket
        </Button>
      }
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 h-auto md:h-[calc(100vh-240px)] min-h-[320px] md:min-h-[420px]">
        {/* Left Side - Tickets List: full width on mobile, fixed width on desktop */}
        <div className="w-full md:w-[320px] lg:w-[380px] md:shrink-0 flex flex-col bg-white dark:bg-primary-card rounded-xl md:rounded-2xl border border-gray-200 dark:border-dark-200 shadow-sm overflow-hidden min-h-[200px] max-h-[40vh] md:max-h-none flex-1 md:flex-none">
          {/* Search */}
          <div className="p-4 border-b border-gray-100 dark:border-dark-200">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-light-500" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-xl text-gray-900 dark:text-light-100 placeholder-gray-400 dark:placeholder-light-500 focus:outline-none focus:ring-2 focus:ring-primary-accent/50 focus:border-primary-accent transition-all text-sm"
              />
            </div>
          </div>

          {/* List header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 dark:border-dark-200 bg-gray-50/50 dark:bg-dark-100/30">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-light-500">
              Tickets
            </span>
            <span className="text-xs font-medium text-gray-400 dark:text-light-500 tabular-nums">
              {filteredTickets.length}
            </span>
          </div>

          {/* Tickets List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-light-500">
                <div className="w-10 h-10 rounded-full border-2 border-primary-accent/30 border-t-primary-accent animate-spin mb-3" />
                <p className="text-sm font-medium">Loading tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-dark-100 flex items-center justify-center mb-3 text-gray-400 dark:text-light-500">
                  <Ticket className="w-7 h-7" />
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-light-400 mb-1">
                  No tickets yet
                </p>
                <p className="text-xs text-gray-500 dark:text-light-500">
                  Create a ticket to get started
                </p>
              </div>
            ) : (
              filteredTickets.map((ticket) => {
                const isSelected = selectedTicket?._id === ticket._id;
                const ticketIdStr = ticket._id?.toString() || ticket._id;
                const hasBeenViewed = viewedTickets.has(ticketIdStr);
                const isNew =
                  ticket.status === "open" && !hasBeenViewed && !isSelected;
                const isClosed = ticket.status === "closed";
                return (
                  <motion.div
                    key={ticket._id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`rounded-xl p-4 cursor-pointer transition-all duration-200 border relative overflow-hidden ${
                      isSelected
                        ? "bg-primary-accent/10 dark:bg-primary-accent/15 border-primary-accent/40 shadow-sm"
                        : isNew
                          ? "border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/15 dark:border-l-blue-400 border-gray-100 dark:border-dark-200 hover:border-gray-200 dark:hover:border-dark-100"
                          : isClosed
                            ? "border-l-4 border-l-red-500 bg-red-50/30 dark:bg-red-900/10 dark:border-l-red-400 border-gray-100 dark:border-dark-200 hover:border-gray-200 dark:hover:border-dark-100 opacity-90"
                            : "border-gray-100 dark:border-dark-200 hover:bg-gray-50 dark:hover:bg-dark-100/50 hover:border-gray-200 dark:hover:border-dark-100"
                    } ${isSelected ? "ring-2 ring-primary-accent/30" : ""}`}
                  >
                    {/* NEW Stamp Overlay */}
                    {isNew && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <div className="transform rotate-[-15deg] scale-110">
                          <span
                            className="inline-block px-8 py-3 text-3xl font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest opacity-25 dark:opacity-35 select-none drop-shadow-lg"
                            style={{
                              textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
                              WebkitTextStroke: "1px rgba(59, 130, 246, 0.3)",
                            }}
                          >
                            NEW
                          </span>
                        </div>
                      </div>
                    )}

                    {/* CLOSED Stamp Overlay */}
                    {isClosed && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <div className="transform rotate-[-15deg] scale-110">
                          <span
                            className="inline-block px-8 py-3 text-3xl font-black text-red-600 dark:text-red-400 uppercase tracking-widest opacity-30 dark:opacity-40 select-none drop-shadow-lg"
                            style={{
                              textShadow: "2px 2px 4px rgba(0,0,0,0.15)",
                              WebkitTextStroke: "1px rgba(220, 38, 38, 0.4)",
                            }}
                          >
                            CLOSED
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="relative z-20">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <span className="text-xs font-semibold text-primary-accent dark:text-orange-400 tabular-nums truncate">
                          #{getTicketDisplayId(ticket)}
                        </span>
                        <Badge
                          variant={getStatusVariant(ticket.status)}
                          size="sm"
                        >
                          {getStatusLabel(ticket.status)}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-light-100 line-clamp-2 mb-2">
                        {ticket.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-light-500">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Side - Ticket Details & Chat: full width, scrollable */}
        <div className="flex-1 flex flex-col min-w-0 min-h-[50vh] md:min-h-0 bg-white dark:bg-primary-card rounded-xl md:rounded-2xl overflow-hidden shadow-sm">
          {selectedTicket ? (
            <>
              {/* Ticket Header */}
              <div className="p-4 border-b border-gray-200/60 dark:border-dark-200/60">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-accent/10 dark:bg-primary-accent/20 text-xs font-bold tracking-wide text-primary-accent dark:text-orange-400 tabular-nums">
                        #{getTicketDisplayId(selectedTicket)}
                      </span>
                      <Badge variant={getStatusVariant(selectedTicket.status)}>
                        {getStatusLabel(selectedTicket.status)}
                      </Badge>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-light-100 mb-1.5 leading-tight">
                      {selectedTicket.title}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasPendingCloseRequest(selectedTicket) &&
                      isCloseRequestedByMe(selectedTicket) && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-medium whitespace-nowrap border border-amber-200/50 dark:border-amber-800/50">
                          Awaiting creator
                        </span>
                      )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDetails(!showDetails)}
                      className="gap-1.5 text-gray-600 dark:text-light-400 hover:text-primary-accent dark:hover:text-primary-accent"
                    >
                      <Info className="w-4 h-4" />
                      {showDetails ? "Hide" : "Show"} Details
                      {showDetails ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    {canCloseTicket(selectedTicket) &&
                      !(
                        hasPendingCloseRequest(selectedTicket) &&
                        isTicketCreator(selectedTicket)
                      ) && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() =>
                            handleCloseTicketClick(selectedTicket._id)
                          }
                          className="px-4 py-2 rounded-lg shadow-none hover:shadow-sm transition-shadow"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Close Ticket
                        </Button>
                      )}
                  </div>
                </div>

                {/* Creator: confirm or cancel close request */}
                {hasPendingCloseRequest(selectedTicket) &&
                  isTicketCreator(selectedTicket) && (
                    <div className="mb-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/40">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-2">
                        {typeof selectedTicket.closeRequestedBy === "object"
                          ? selectedTicket.closeRequestedBy?.fullNameEn ||
                            selectedTicket.closeRequestedBy?.nickname ||
                            "Someone"
                          : "Someone"}{" "}
                        requested to close this ticket.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleConfirmClose(selectedTicket._id)}
                          className="shadow-none"
                        >
                          Confirm close
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelClose(selectedTicket._id)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                {/* Meta */}
                {showDetails && (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm pt-3 mt-3 border-t border-gray-200/60 dark:border-dark-200/60">
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-light-500 mb-1">
                        Created by
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-light-100">
                        {selectedTicket.createdBy?.fullNameEn ||
                          selectedTicket.createdBy?.nickname ||
                          "Unknown"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-light-500 mb-1">
                        Created at
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-light-100">
                        {new Date(selectedTicket.createdAt).toLocaleString()}
                      </p>
                    </div>
                    {selectedTicket.assignedTo &&
                      selectedTicket.assignedTo.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-xs font-medium text-gray-500 dark:text-light-500 mb-1">
                            Assigned to
                          </p>
                          <p className="font-semibold text-gray-900 dark:text-light-100">
                            {selectedTicket.assignedTo
                              .map((emp) =>
                                typeof emp === "string"
                                  ? emp
                                  : emp.fullNameEn || emp.nickname || "Unknown",
                              )
                              .join(", ")}
                          </p>
                        </div>
                      )}
                    {selectedTicket.assignedTeams &&
                      selectedTicket.assignedTeams.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-xs font-medium text-gray-500 dark:text-light-500 mb-1">
                            Teams
                          </p>
                          <p className="font-semibold text-gray-900 dark:text-light-100">
                            {selectedTicket.assignedTeams
                              .map((teamValue) => {
                                const teamLabels = {
                                  backend: "Backend",
                                  frontend: "Frontend",
                                  mobile_app: "Mobile App",
                                  uiux: "UI/UX",
                                  tester: "Tester",
                                  ra: "RA",
                                };
                                return teamLabels[teamValue] || teamValue;
                              })
                              .join(", ")}
                          </p>
                        </div>
                      )}
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-light-500">
                          Participants
                        </p>
                        {isTicketCreator(selectedTicket) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleOpenParticipantsModal}
                            className="text-xs px-2 py-1 h-auto"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add
                          </Button>
                        )}
                      </div>
                      {selectedTicket.participants &&
                      selectedTicket.participants.length > 0 ? (
                        <p className="font-semibold text-gray-900 dark:text-light-100">
                          {selectedTicket.participants
                            .map((emp) =>
                              typeof emp === "string"
                                ? emp
                                : emp.fullNameEn || emp.nickname || "Unknown",
                            )
                            .join(", ")}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-light-500 italic">
                          No participants yet
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
                {/* Messages from API */}
                {(selectedTicket.messages || []).map((msg) => {
                  const fromId =
                    typeof msg.from === "object" ? msg.from?._id : msg.from;
                  const isMe = fromId === currentUserId;
                  const name =
                    typeof msg.from === "object"
                      ? msg.from?.fullNameEn || msg.from?.nickname || "Unknown"
                      : "Unknown";
                  return (
                    <div
                      key={
                        msg._id || msg.createdAt?.toString() || Math.random()
                      }
                      className={`group flex items-start gap-3 ${isMe ? "flex-row-reverse" : ""}`}
                    >
                      <Avatar src={typeof msg.from === "object" ? msg.from?.photo : undefined} name={name} className="!w-9 !h-9" />
                      <div
                        className={`flex-1 min-w-0 flex flex-col ${isMe ? "items-end" : "items-start"}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap justify-end">
                          <span className="font-semibold text-gray-900 dark:text-light-100 text-sm">
                            {name}
                            {isMe && " (You)"}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-light-500">
                            {new Date(msg.createdAt).toLocaleString()}
                          </span>
                          {isMe &&
                            selectedTicket.status === "open" &&
                            editingMessageId !== msg._id && (
                              <button
                                type="button"
                                title="Edit message"
                                aria-label="Edit message"
                                onClick={() => {
                                  setEditingMessageId(msg._id);
                                  setEditingText(msg.text || "");
                                }}
                                className="p-1 rounded-md text-gray-400 dark:text-light-500 hover:text-primary-accent hover:bg-primary-accent/10 dark:hover:bg-primary-accent/15 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                        </div>
                        <div
                          className={`p-4 max-w-[85%] space-y-2 ${
                            isMe
                              ? "rounded-2xl rounded-tr-sm bg-primary-accent/12 dark:bg-primary-accent/15 text-gray-900 dark:text-light-100 border border-primary-accent/20 dark:border-primary-accent/25"
                              : "rounded-2xl rounded-tl-sm bg-white dark:bg-dark-300/40 text-gray-700 dark:text-light-300 border border-gray-200/60 dark:border-dark-200/40"
                          }`}
                        >
                          {editingMessageId === msg._id ? (
                            <div className="space-y-2 min-w-[200px]">
                              <textarea
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveEdit(msg._id);
                                  }
                                  if (e.key === "Escape") {
                                    setEditingMessageId(null);
                                    setEditingText("");
                                  }
                                }}
                                rows={Math.min(
                                  6,
                                  (editingText.match(/\n/g) || []).length + 2,
                                )}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus
                                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-dark-400 border border-primary-accent/50 text-sm text-gray-900 dark:text-light-100 resize-none focus:outline-none focus:ring-2 focus:ring-primary-accent/40"
                              />
                              <div className="flex gap-2 justify-end">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingMessageId(null);
                                    setEditingText("");
                                  }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 dark:text-light-500 hover:bg-gray-200/60 dark:hover:bg-dark-300 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSaveEdit(msg._id)}
                                  disabled={!editingText.trim()}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-accent text-white hover:bg-primary-accent/90 disabled:opacity-40 transition-colors"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            msg.text && (
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {renderMessageText(msg.text)}
                              </p>
                            )
                          )}
                          {msg.attachment && (
                            <div className="mt-2">
                              {(msg.attachment.contentType || "").startsWith(
                                "image/",
                              ) ? (
                                <a
                                  href={`data:${msg.attachment.contentType};base64,${msg.attachment.data}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block rounded-lg overflow-hidden border border-gray-200 dark:border-dark-200 max-w-xs"
                                >
                                  <img
                                    src={`data:${msg.attachment.contentType};base64,${msg.attachment.data}`}
                                    alt={msg.attachment.filename || "Image"}
                                    className="max-h-48 w-auto object-cover"
                                  />
                                </a>
                              ) : (
                                <a
                                  href={`data:${msg.attachment.contentType};base64,${msg.attachment.data}`}
                                  download={msg.attachment.filename || "file"}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/50 dark:bg-black/20 border border-gray-200 dark:border-dark-200 text-sm font-medium text-primary-accent hover:underline"
                                >
                                  <Paperclip className="w-4 h-4 shrink-0" />
                                  {msg.attachment.filename || "Download"}
                                </a>
                              )}
                            </div>
                          )}
                          {msg.attachmentUrl &&
                            (() => {
                              const url = msg.attachmentUrl;
                              const isImage =
                                /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url);
                              const displayName =
                                msg.attachmentFilename ||
                                decodeURIComponent(
                                  url.split("/").pop() || "file",
                                ).replace(/^\d+-/, "") ||
                                "file";
                              return isImage ? (
                                <div className="mt-2">
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded-lg overflow-hidden border border-gray-200 dark:border-dark-200 max-w-xs"
                                  >
                                    <img
                                      src={url}
                                      alt={displayName}
                                      className="max-h-48 w-auto object-cover"
                                    />
                                  </a>
                                </div>
                              ) : (
                                <a
                                  href={url}
                                  download={displayName}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/50 dark:bg-black/20 border border-gray-200 dark:border-dark-200 text-sm font-medium text-primary-accent hover:underline"
                                >
                                  <FileText className="w-4 h-4 shrink-0" />
                                  <span className="truncate max-w-[200px]">
                                    {displayName}
                                  </span>
                                  <Download className="w-3.5 h-3.5 shrink-0" />
                                </a>
                              );
                            })()}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Status: in progress */}
                {selectedTicket.status === "in_progress" && (
                  <div className="flex items-center justify-center py-2">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/10 border border-blue-200/40 dark:border-blue-800/30">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                        In Progress
                      </span>
                      <span className="text-xs text-blue-600/70 dark:text-blue-400/70">
                        •{" "}
                        {new Date(
                          selectedTicket.updatedAt,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Status: closed */}
                {selectedTicket.status === "closed" && (
                  <div className="flex items-center justify-center py-2">
                    <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200/40 dark:border-emerald-800/30">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                        Closed
                        {selectedTicket.closedBy && (
                          <span className="font-normal">
                            {" "}
                            by{" "}
                            {typeof selectedTicket.closedBy === "object"
                              ? selectedTicket.closedBy?.fullNameEn ||
                                selectedTicket.closedBy?.nickname ||
                                "support"
                              : "support"}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                        •{" "}
                        {selectedTicket.closedAt
                          ? new Date(
                              selectedTicket.closedAt,
                            ).toLocaleDateString()
                          : new Date(
                              selectedTicket.updatedAt,
                            ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Message Input — disabled when closed or when assignee requested close */}
              <div className="p-5 border-t border-gray-200/60 dark:border-dark-200/60 bg-gray-50/50 dark:bg-dark-100/30">
                {!canSendMessage(selectedTicket) ? (
                  <p className="text-sm text-gray-500 dark:text-light-500 text-center py-3 px-4 bg-gray-100 dark:bg-dark-200/40 rounded-xl">
                    {selectedTicket.status === "closed"
                      ? "This ticket is closed. No new messages can be sent."
                      : "You requested to close this ticket. You cannot send messages until the creator confirms or cancels."}
                  </p>
                ) : (
                  <div className="flex items-end gap-3">
                    <Avatar src={auth?.employee?.photo} name={auth?.employee?.fullNameEn || auth?.employee?.nickname} className="!w-9 !h-9" />
                    <div className="flex-1 flex flex-col gap-2.5">
                      {/* Focus ring on container so "active" state is clear */}
                      <div className="relative rounded-2xl bg-white dark:bg-dark-300/50 border border-gray-300/60 dark:border-dark-200/60 transition-all duration-200 focus-within:ring-2 focus-within:ring-primary-accent/50 focus-within:border-primary-accent dark:focus-within:border-primary-accent/60">
                        <input
                          type="text"
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (!isSendingRef.current) handleSendMessage();
                            }
                          }}
                          className="w-full px-4 py-3 pr-24 rounded-2xl bg-transparent text-gray-900 dark:text-light-100 placeholder-gray-500 dark:placeholder-light-500 focus:outline-none"
                        />
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx,.txt,.zip"
                          onChange={handleFileChange}
                          className="hidden"
                          aria-hidden
                        />
                        {/* Hidden folder picker — .csv only */}
                        <input
                          ref={folderInputRef}
                          type="file"
                          // @ts-ignore – webkitdirectory is non-standard but broadly supported
                          webkitdirectory=""
                          onChange={handleFolderChange}
                          className="hidden"
                          aria-hidden
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                          <button
                            type="button"
                            title="Upload CSV folder"
                            onClick={() => folderInputRef.current?.click()}
                            className="p-2 rounded-lg text-gray-500 dark:text-light-500 hover:text-primary-accent hover:bg-primary-accent/10 dark:hover:bg-primary-accent/15 transition-colors"
                            aria-label="Upload CSV folder"
                          >
                            <FolderOpen className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 rounded-lg text-gray-500 dark:text-light-500 hover:text-primary-accent hover:bg-primary-accent/10 dark:hover:bg-primary-accent/15 transition-colors"
                            aria-label="Attach file"
                          >
                            <Paperclip className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSendMessage()}
                            disabled={
                              (!newMessage.trim() && !attachmentFile) ||
                              isUploadingFile ||
                              folderUploading ||
                              folderZipping
                            }
                            className="p-2 rounded-lg bg-primary-accent/10 dark:bg-primary-accent/15 text-primary-accent hover:bg-primary-accent hover:text-white disabled:opacity-40 disabled:hover:bg-primary-accent/10 dark:disabled:hover:bg-primary-accent/15 disabled:hover:text-primary-accent transition-all"
                            aria-label="Send message"
                          >
                            {isUploadingFile ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      {/* Single-file attachment preview chip */}
                      {attachmentFile && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-accent/10 dark:bg-primary-accent/15 text-primary-accent dark:text-orange-400 text-sm font-medium border border-primary-accent/20 dark:border-primary-accent/25">
                            <Paperclip className="w-3.5 h-3.5" />
                            {attachmentFile.name}
                            <span className="text-xs opacity-70">
                              ({(attachmentFile.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                              type="button"
                              onClick={removeAttachment}
                              className="p-0.5 rounded-md hover:bg-primary-accent/20 dark:hover:bg-primary-accent/25 transition-colors"
                              aria-label="Remove attachment"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                          <span className="text-xs text-gray-500 dark:text-light-500">
                            Images, PDF, CSV, DOC, XLS — uploaded to Supabase
                          </span>
                        </div>
                      )}
                      {/* Folder progress chip */}
                      {(folderZipping || folderUploading) && (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-medium border border-emerald-500/20">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {folderZipping
                              ? "Zipping folder..."
                              : "Uploading zip..."}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center h-full px-6 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-dark-100 flex items-center justify-center mb-4 text-gray-400 dark:text-light-500">
                <MessageSquare className="w-10 h-10" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 dark:text-light-300 mb-1">
                No ticket selected
              </h3>
              <p className="text-sm text-gray-500 dark:text-light-500 max-w-xs">
                Select a ticket from the list to view details and conversation
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Folder Upload Confirmation Modal */}
      <BaseModal
        isOpen={!!folderConfirmation}
        onClose={clearFolderFiles}
        title="Upload Folder as ZIP"
        subtitle={`${folderConfirmation?.fileCount ?? 0} file${(folderConfirmation?.fileCount ?? 0) !== 1 ? "s" : ""} from "${folderConfirmation?.name ?? ""}"`}
        size="sm"
      >
        <div className="space-y-4">
          <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 dark:border-dark-200 bg-gray-50 dark:bg-dark-100 px-3 py-2 space-y-1">
            {folderConfirmation?.files.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-light-200"
              >
                <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                <span className="truncate">
                  {f.webkitRelativePath || f.name}
                </span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 dark:text-light-400">
            All files will be compressed into{" "}
            <span className="font-medium text-gray-700 dark:text-light-200">
              {folderConfirmation?.name}.zip
            </span>{" "}
            and sent as a single attachment.
          </p>
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={clearFolderFiles}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-dark-200 text-gray-700 dark:text-light-200 hover:bg-gray-50 dark:hover:bg-dark-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmFolder}
              className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Zip &amp; Send
            </button>
          </div>
        </div>
      </BaseModal>

      {/* Create New Ticket Modal */}
      <BaseModal
        isOpen={isOpen}
        onClose={isCreatingTicket ? () => {} : closeModal}
        title="Create New Ticket"
        subtitle="Tell us more about your issue"
        size="md"
      >
        <div className="space-y-5">
          {isCreatingTicket && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-primary-accent/10 dark:bg-primary-accent/15 border border-primary-accent/20">
              <Loader2 className="w-5 h-5 animate-spin text-primary-accent shrink-0" />
              <p className="text-sm font-medium text-gray-700 dark:text-light-200">
                Creating ticket...
              </p>
            </div>
          )}
          <Input
            label="Ticket Title"
            placeholder="Enter ticket title"
            value={newTicketData.title}
            onChange={(e) =>
              setNewTicketData((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            disabled={isCreatingTicket}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={newTicketData.description}
              onChange={(e) =>
                setNewTicketData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Provide detailed information about your issue..."
              rows={4}
              disabled={isCreatingTicket}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-primary-card text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent resize-none disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Assign to
            </label>

            {/* Toggle between Employees and Teams */}
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setAssignType("employees")}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  assignType === "employees"
                    ? "bg-primary-accent text-white"
                    : "bg-gray-100 dark:bg-dark-100 text-gray-700 dark:text-light-300 hover:bg-gray-200 dark:hover:bg-dark-200"
                }`}
              >
                Employees
              </button>
              <button
                type="button"
                onClick={() => setAssignType("teams")}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  assignType === "teams"
                    ? "bg-primary-accent text-white"
                    : "bg-gray-100 dark:bg-dark-100 text-gray-700 dark:text-light-300 hover:bg-gray-200 dark:hover:bg-dark-200"
                }`}
              >
                Teams
              </button>
            </div>

            <div className="relative mb-2">
              <button
                type="button"
                onClick={() =>
                  !isCreatingTicket &&
                  setIsAssignDropdownOpen(!isAssignDropdownOpen)
                }
                disabled={isCreatingTicket}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-dark-200 bg-white dark:bg-primary-card text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="text-sm">
                  {assignType === "employees"
                    ? newTicketData.assignedTo.length > 0
                      ? `${newTicketData.assignedTo.length} employee(s) selected`
                      : "Select employees"
                    : newTicketData.assignedTeams.length > 0
                      ? `${newTicketData.assignedTeams.length} team(s) selected`
                      : "Select teams"}
                </span>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </button>

              {isAssignDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  {assignType === "employees" && (
                    <div className="sticky top-0 p-2 bg-white dark:bg-primary-card border-b border-gray-300 dark:border-dark-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={employeeSearchQuery}
                          onChange={(e) =>
                            setEmployeeSearchQuery(e.target.value)
                          }
                          placeholder="Search employees..."
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  )}
                  <div className="max-h-52 overflow-y-auto">
                    {assignType === "employees"
                      ? employees
                          .filter((employee) => employee._id !== currentUserId)
                          .filter((employee) =>
                            employee.fullName
                              ?.toLowerCase()
                              .includes(employeeSearchQuery.toLowerCase()),
                          )
                          .map((employee) => (
                            <label
                              key={employee._id}
                              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={newTicketData.assignedTo.includes(
                                  employee._id,
                                )}
                                onChange={() => toggleAssignee(employee._id)}
                                className="w-4 h-4 text-primary-accent rounded border-gray-300 focus:ring-2 focus:ring-primary-accent"
                              />
                              <span className="text-sm text-gray-900 dark:text-light-100">
                                {employee.fullName}
                              </span>
                            </label>
                          ))
                      : teams.map((team) => (
                          <label
                            key={team.value}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-200 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={newTicketData.assignedTeams.includes(
                                team.value,
                              )}
                              onChange={() => toggleTeam(team.value)}
                              className="w-4 h-4 text-primary-accent rounded border-gray-300 focus:ring-2 focus:ring-primary-accent"
                            />
                            <span className="text-sm text-gray-900 dark:text-light-100">
                              {team.label}
                            </span>
                          </label>
                        ))}
                  </div>
                </div>
              )}
            </div>

            {/* Selected Items Display */}
            <div className="flex flex-wrap gap-2">
              {assignType === "employees"
                ? newTicketData.assignedTo.map((assigneeId) => {
                    const employee = employees.find(
                      (emp) => emp._id === assigneeId,
                    );
                    return employee ? (
                      <span
                        key={assigneeId}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                      >
                        {employee.fullName}
                        <button
                          onClick={() => removeAssignee(assigneeId)}
                          className="hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ) : null;
                  })
                : newTicketData.assignedTeams.map((teamValue) => {
                    const team = teams.find((t) => t.value === teamValue);
                    return team ? (
                      <span
                        key={teamValue}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm"
                      >
                        {team.label}
                        <button
                          onClick={() => removeTeam(teamValue)}
                          className="hover:text-purple-600 dark:hover:text-purple-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ) : null;
                  })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-100">
            <Button
              variant="bordered"
              onClick={closeModal}
              size="lg"
              disabled={isCreatingTicket}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTicket}
              size="lg"
              disabled={isCreatingTicket}
            >
              {isCreatingTicket ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </div>
      </BaseModal>

      {/* Already closed by someone else */}
      <BaseModal
        isOpen={alreadyClosedModal.open}
        onClose={() => setAlreadyClosedModal({ open: false, ticket: null })}
        title="Ticket already closed"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={handleAlreadyClosedContinue}>
              Continue Ticket
            </Button>
            <Button variant="primary" onClick={handleAlreadyClosedCloseTicket}>
              Close Ticket
            </Button>
          </>
        }
      >
        <p className="text-gray-700 dark:text-light-200">
          This ticket has been closed by someone else. You can leave the view or
          continue viewing the closed ticket.
        </p>
      </BaseModal>

      {/* Add Participants Modal */}
      <BaseModal
        isOpen={isParticipantsModalOpen}
        onClose={isAddingParticipants ? () => {} : handleCloseParticipantsModal}
        title="Add Participants"
        subtitle="Select users to add as participants to this ticket"
        size="md"
      >
        <div className="space-y-5">
          {isAddingParticipants && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-primary-accent/10 dark:bg-primary-accent/15 border border-primary-accent/20">
              <Loader2 className="w-5 h-5 animate-spin text-primary-accent shrink-0" />
              <p className="text-sm font-medium text-gray-700 dark:text-light-200">
                Adding participants...
              </p>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-light-500" />
            <input
              type="text"
              placeholder="Search employees..."
              value={participantSearchQuery}
              onChange={(e) => setParticipantSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-xl text-gray-900 dark:text-light-100 placeholder-gray-400 dark:placeholder-light-500 focus:outline-none focus:ring-2 focus:ring-primary-accent/50 focus:border-primary-accent transition-all text-sm"
              disabled={isAddingParticipants}
            />
          </div>

          {/* Employee List */}
          <div className="border border-gray-200 dark:border-dark-200 rounded-xl max-h-80 overflow-y-auto">
            {employees
              .filter((emp) => {
                // Filter out current user, creator, assigned users, and existing participants
                const empId = emp._id;
                if (empId === currentUserId) return false;
                if (empId === creatorId(selectedTicket)) return false;
                if (
                  selectedTicket?.assignedTo?.some(
                    (assigned) =>
                      (typeof assigned === "string"
                        ? assigned
                        : assigned._id) === empId,
                  )
                )
                  return false;
                if (
                  selectedTicket?.participants?.some(
                    (participant) =>
                      (typeof participant === "string"
                        ? participant
                        : participant._id) === empId,
                  )
                )
                  return false;

                // Search filter
                const query = participantSearchQuery.toLowerCase().trim();
                if (!query) return true;
                const fullName = (
                  emp.fullNameEn ||
                  emp.nickname ||
                  ""
                ).toLowerCase();
                return fullName.includes(query);
              })
              .map((emp, index) => (
                <div
                  key={emp._id}
                  className={`flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-dark-100/50 cursor-pointer transition-colors ${
                    index > 0
                      ? "border-t border-gray-100 dark:border-dark-200"
                      : ""
                  }`}
                  onClick={() =>
                    !isAddingParticipants && toggleParticipant(emp._id)
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedParticipants.includes(emp._id)}
                    onChange={() => toggleParticipant(emp._id)}
                    className="w-4 h-4 text-primary-accent bg-gray-100 border-gray-300 rounded focus:ring-primary-accent dark:focus:ring-primary-accent dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    disabled={isAddingParticipants}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                      {emp.fullNameEn || emp.nickname || "Unknown"}
                    </p>
                    {emp.email && (
                      <p className="text-xs text-gray-500 dark:text-light-500 truncate">
                        {emp.email}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            {employees.filter((emp) => {
              const empId = emp._id;
              if (empId === currentUserId) return false;
              if (empId === creatorId(selectedTicket)) return false;
              if (
                selectedTicket?.assignedTo?.some(
                  (assigned) =>
                    (typeof assigned === "string" ? assigned : assigned._id) ===
                    empId,
                )
              )
                return false;
              if (
                selectedTicket?.participants?.some(
                  (participant) =>
                    (typeof participant === "string"
                      ? participant
                      : participant._id) === empId,
                )
              )
                return false;
              const query = participantSearchQuery.toLowerCase().trim();
              if (!query) return true;
              const fullName = (
                emp.fullNameEn ||
                emp.nickname ||
                ""
              ).toLowerCase();
              return fullName.includes(query);
            }).length === 0 && (
              <div className="p-6 text-center text-gray-500 dark:text-light-500">
                <p className="text-sm">No available employees to add</p>
              </div>
            )}
          </div>

          {/* Selected Count */}
          {selectedParticipants.length > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                {selectedParticipants.length} participant
                {selectedParticipants.length !== 1 ? "s" : ""} selected
              </p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-100">
            <Button
              variant="bordered"
              onClick={handleCloseParticipantsModal}
              size="lg"
              disabled={isAddingParticipants}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddParticipants}
              size="lg"
              disabled={
                isAddingParticipants || selectedParticipants.length === 0
              }
            >
              {isAddingParticipants ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Participants
                </>
              )}
            </Button>
          </div>
        </div>
      </BaseModal>
    </PageWrapper>
  );
};

export default Tickets;
